// Typing: one ProseMirror per text node, bound to its LoroText. A keystroke
// changes ProseMirror first, so it paints at once, and becomes the same
// change on the LoroText in the same task, so it travels as soon as it can.
// A change from anyone else arrives as a delta and is applied the other way.
// meets: together/fast
import type { LoroText, Delta } from "loro-crdt";
import { Schema, type Mark, type Node as PMNode } from "prosemirror-model";
import { EditorState, Plugin, PluginKey, TextSelection, type Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { safeHref } from "./href";
import { AddMarkStep, RemoveMarkStep, ReplaceStep } from "prosemirror-transform";
import { keymap } from "prosemirror-keymap";
import { baseKeymap, toggleMark } from "prosemirror-commands";
import { MARKS } from "./doc";

// A text node holds inline text and marks, nothing else.
export const schema = new Schema({
  nodes: { doc: { content: "text*" }, text: {} },
  marks: {
    bold: { toDOM: () => ["strong", 0], parseDOM: [{ tag: "strong" }, { tag: "b" }] },
    italic: { toDOM: () => ["em", 0], parseDOM: [{ tag: "em" }, { tag: "i" }] },
    code: { toDOM: () => ["code", 0], parseDOM: [{ tag: "code" }] },
    link: {
      attrs: { href: {} },
      inclusive: false,
      toDOM: (m) => ["a", { href: safeHref(m.attrs.href) }, 0],
      parseDOM: [{ tag: "a[href]", getAttrs: (d) => ({ href: safeHref((d as HTMLElement).getAttribute("href") ?? "") }) }],
    },
  },
});

export { safeHref };

type MarkValue = true | string;
const markValue = (m: Mark): MarkValue => (m.type.name === "link" ? safeHref(m.attrs.href) : true);
function marksOf(attrs: Record<string, unknown> | undefined): Mark[] {
  const out: Mark[] = [];
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v == null || v === false || !(k in MARKS)) continue;
    out.push(k === "link" ? schema.marks.link.create({ href: safeHref(v) }) : schema.marks[k].create());
  }
  return out;
}

export function docFrom(delta: Delta<string>[]): PMNode {
  const nodes = delta.flatMap((op) => ("insert" in op && op.insert ? [schema.text(op.insert, marksOf(op.attributes))] : []));
  return schema.node("doc", null, nodes);
}

// ProseMirror's steps as LoroText operations. In a doc of inline text a
// ProseMirror position is the UTF-16 offset Loro uses, so they map 1:1.
function applySteps(text: LoroText, tr: Transaction) {
  tr.steps.forEach((step) => {
    if (step instanceof ReplaceStep) {
      const { from, to, slice } = step as unknown as { from: number; to: number; slice: { content: PMNode["content"] } };
      if (to > from) text.delete(from, to - from);
      let at = from;
      slice.content.forEach((n) => {
        if (!n.isText || !n.text) return;
        const end = at + n.text.length;
        text.insert(at, n.text);
        // Loro grows a mark over text typed at its edge by its own rule;
        // make the inserted range carry exactly ProseMirror's marks.
        const want = new Map(n.marks.map((m) => [m.type.name, markValue(m)]));
        const have = new Set(text.sliceDelta(at, end).flatMap((op) => Object.keys(op.attributes ?? {})));
        for (const k of have) if (!want.has(k)) text.unmark({ start: at, end }, k);
        for (const [k, v] of want) text.mark({ start: at, end }, k, v);
        at = end;
      });
    } else if (step instanceof AddMarkStep) {
      const s = step as unknown as { from: number; to: number; mark: Mark };
      text.mark({ start: s.from, end: s.to }, s.mark.type.name, markValue(s.mark));
    } else if (step instanceof RemoveMarkStep) {
      const s = step as unknown as { from: number; to: number; mark: Mark };
      text.unmark({ start: s.from, end: s.to }, s.mark.type.name);
    } else {
      // A step this doc's schema shouldn't produce: write the result whole.
      const doc = tr.doc;
      text.delete(0, text.length);
      text.applyDelta(deltaOf(doc));
    }
  });
}

function deltaOf(doc: PMNode): Delta<string>[] {
  const out: Delta<string>[] = [];
  doc.forEach((n) => {
    if (!n.text) return;
    const attributes = Object.fromEntries(n.marks.map((m) => [m.type.name, markValue(m)]));
    out.push(n.marks.length ? { insert: n.text, attributes } : { insert: n.text });
  });
  return out;
}

// A delta from Loro as one ProseMirror transaction.
function fromDelta(state: EditorState, delta: Delta<string>[]): Transaction {
  const tr = state.tr;
  let pos = 0;
  for (const op of delta) {
    if ("retain" in op && op.retain !== undefined) {
      for (const [k, v] of Object.entries(op.attributes ?? {})) {
        if (!(k in MARKS)) continue;
        if (v == null) tr.removeMark(pos, pos + op.retain, schema.marks[k]);
        else tr.addMark(pos, pos + op.retain, marksOf({ [k]: v })[0]);
      }
      pos += op.retain;
    } else if ("insert" in op && op.insert) {
      tr.insert(pos, schema.text(op.insert, marksOf(op.attributes)));
      pos += op.insert.length;
    } else if ("delete" in op && op.delete) {
      tr.delete(pos, pos + op.delete);
    }
  }
  return tr;
}

export type TextHooks = {
  commit(): void; // commit the doc's pending transaction
  undo(): boolean;
  redo(): boolean;
  enter(at: number): void; // split here
  backspaceAtStart(): void; // join with the text before
  selection?(from: number, to: number): void; // for presence
  keydown?(e: KeyboardEvent): boolean;
  slash?(): void; // `/` in an empty text: the insert menu
};

// Ranges drawn over the text, such as comments: decorations, never marks,
// so they are no part of the page.
export type Highlight = { from: number; to: number; class: string; id: string };
const highlights = new PluginKey<DecorationSet>("highlights");
const highlightPlugin = new Plugin<DecorationSet>({
  key: highlights,
  state: {
    init: () => DecorationSet.empty,
    apply: (tr, set) => (tr.getMeta(highlights) as DecorationSet | undefined) ?? set.map(tr.mapping, tr.doc),
  },
  props: { decorations: (state) => highlights.getState(state) },
});

export class TextView {
  readonly view: EditorView;
  private applying = false;
  private unsubscribe: () => void;

  constructor(readonly dom: HTMLElement, readonly text: LoroText, hooks: TextHooks) {
    const keys = keymap({
      "Mod-z": () => hooks.undo(),
      "Mod-y": () => hooks.redo(),
      "Shift-Mod-z": () => hooks.redo(),
      "Mod-b": toggleMark(schema.marks.bold),
      "Mod-i": toggleMark(schema.marks.italic),
      "Mod-e": toggleMark(schema.marks.code),
      // The start and end of a block's words, set here rather than left to
      // the browser, so the selection is ProseMirror's at once.
      "Mod-Home": (state, dispatch) => (dispatch?.(state.tr.setSelection(TextSelection.atStart(state.doc)).scrollIntoView()), true),
      "Mod-End": (state, dispatch) => (dispatch?.(state.tr.setSelection(TextSelection.atEnd(state.doc)).scrollIntoView()), true),
      Enter: (state) => (hooks.enter(state.selection.from), true),
      "/": (state) => {
        if (state.doc.content.size || !hooks.slash) return false;
        hooks.slash();
        return true;
      },
      Backspace: (state) => {
        if (!state.selection.empty || state.selection.from !== 0) return false;
        hooks.backspaceAtStart();
        return true;
      },
    });
    const self = this;
    this.view = new EditorView(
      { mount: dom },
      {
        state: EditorState.create({ schema, doc: docFrom(text.toDelta()), plugins: [keys, keymap(baseKeymap), highlightPlugin] }),
        handleKeyDown: (_v, e) => hooks.keydown?.(e) ?? false,
        dispatchTransaction(tr) {
          const view = self.view;
          view.updateState(view.state.apply(tr));
          if (tr.docChanged && !tr.getMeta("remote")) {
            self.applying = true;
            try {
              applySteps(text, tr);
              hooks.commit();
            } finally {
              self.applying = false;
            }
          }
          if (tr.selectionSet || tr.docChanged) hooks.selection?.(view.state.selection.from, view.state.selection.to);
        },
      },
    );
    // A caret the browser moved itself (arrows, Home, a click) reaches
    // ProseMirror by a later `selectionchange`, which a busy page can be
    // slow to deliver; read it as the key or button comes up, so presence
    // and comments see where the caret really is.
    const read = () => (this.view as unknown as { domObserver: { flush(): void } }).domObserver.flush();
    dom.addEventListener("keyup", read);
    dom.addEventListener("mouseup", read);
    this.unsubscribe = text.subscribe((batch) => {
      if (this.applying) return;
      for (const e of batch.events) if (e.diff.type === "text") this.remote(e.diff.diff);
    });
  }

  private remote(delta: Delta<string>[]) {
    const tr = fromDelta(this.view.state, delta).setMeta("remote", true).setMeta("addToHistory", false);
    // Text from another tab that lands at the caret goes after it: the
    // caret stays with what this tab typed, so two people typing at one
    // spot at once each keep their words whole, not interleaved.
    const sel = this.view.state.selection;
    if (sel.empty && tr.docChanged) tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(sel.head, -1)));
    this.view.dispatch(tr);
    // If the two ever disagree, Loro is the truth.
    if (this.view.state.doc.textContent !== this.text.toString()) {
      const doc = docFrom(this.text.toDelta());
      const fix = this.view.state.tr.replaceWith(0, this.view.state.doc.content.size, doc.content).setMeta("remote", true);
      this.view.dispatch(fix);
    }
  }

  highlight(ranges: Highlight[]) {
    const size = this.view.state.doc.content.size;
    const key = JSON.stringify(ranges);
    if (key === this.drawn) return;
    this.drawn = key;
    const set = DecorationSet.create(
      this.view.state.doc,
      ranges
        .filter((r) => r.to > r.from && r.from >= 0 && r.to <= size)
        .map((r) => Decoration.inline(r.from, r.to, { class: r.class, "data-comment": r.id })),
    );
    this.view.dispatch(this.view.state.tr.setMeta(highlights, set).setMeta("remote", true).setMeta("addToHistory", false));
  }
  private drawn = "[]";

  focus(at?: number) {
    if (at !== undefined) {
      const pos = Math.max(0, Math.min(at, this.view.state.doc.content.size));
      this.view.dispatch(this.view.state.tr.setSelection(TextSelection.create(this.view.state.doc, pos)));
    }
    this.view.focus();
  }

  destroy() {
    this.unsubscribe();
    this.view.destroy();
  }
}
